import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import './AsistenciaDashboard.css';

const SCANNER_ID = 'qr-reader';

const ESTADO = {
  ESCANEANDO: 'escaneando',
  CARGANDO:   'cargando',
  RESULTADO:  'resultado',
  ERROR:      'error',
};

export default function AsistenciaDashboard() {
  const { user, logout } = useAuth();
  const html5QrRef   = useRef(null);
  const scannerActive = useRef(false);

  const [estado,   setEstado]   = useState(ESTADO.ESCANEANDO);
  const [persona,  setPersona]  = useState(null);   // { nombre, foto, tipo }
  const [error,    setError]    = useState('');

  // ── arrancar escáner ──────────────────────────────────────────────────────
  const iniciarScanner = useCallback(async () => {
    if (scannerActive.current) return;

    // Pequeño delay para que el DOM esté listo
    await new Promise(r => setTimeout(r, 300));

    const qr = new Html5Qrcode(SCANNER_ID);
    html5QrRef.current = qr;

    try {
      await qr.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 260, height: 260 } },
        async (decodedText) => {
          if (!scannerActive.current) return;
          await detenerScanner();
          await procesarQR(decodedText);
        },
        () => {} // errores de fotograma — ignorar
      );
      scannerActive.current = true;
    } catch (err) {
      console.error('Error al iniciar cámara:', err);
      setError('No se pudo acceder a la cámara. Revisa los permisos del navegador.');
      setEstado(ESTADO.ERROR);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── detener escáner ───────────────────────────────────────────────────────
  const detenerScanner = async () => {
    if (html5QrRef.current && scannerActive.current) {
      try {
        await html5QrRef.current.stop();
        html5QrRef.current.clear();
      } catch (_) {}
      scannerActive.current = false;
      html5QrRef.current = null;
    }
  };

  // ── ciclo de vida ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (estado === ESTADO.ESCANEANDO) iniciarScanner();
    return () => { detenerScanner(); };
  }, [estado]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── procesar QR decodificado ──────────────────────────────────────────────
  const procesarQR = async (texto) => {
    setEstado(ESTADO.CARGANDO);
    try {
      // Extraer el ID del QR: puede ser URL completa o solo el ID
      let id = texto.trim();
      const match = texto.match(/[?&]id=([a-f0-9]{24})/i) || texto.match(/\/([a-f0-9]{24})$/i);
      if (match) id = match[1];

      const { data } = await api.get(`/asistencia/${id}`);
      setPersona(data);
      setEstado(ESTADO.RESULTADO);
    } catch (err) {
      const msg = err.response?.data?.error || 'No se encontró el registro en el sistema.';
      setError(msg);
      setEstado(ESTADO.ERROR);
    }
  };

  // ── volver a escanear ─────────────────────────────────────────────────────
  const escanearSiguiente = async () => {
    await detenerScanner();
    setPersona(null);
    setError('');
    setEstado(ESTADO.ESCANEANDO);
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="asis-root">

      {/* Header */}
      <header className="asis-header">
        <span className="asis-header-title">CONTROL DE ASISTENCIA</span>
        <button className="asis-btn-logout" onClick={logout}>Salir</button>
      </header>

      {/* ── ESCANEANDO ─────────────────────────────────────── */}
      {estado === ESTADO.ESCANEANDO && (
        <div className="asis-scanner-wrap">
          <p className="asis-instruccion">Apunta la cámara al código QR del gafete</p>
          <div id={SCANNER_ID} className="asis-video-container" />
        </div>
      )}

      {/* ── CARGANDO ───────────────────────────────────────── */}
      {estado === ESTADO.CARGANDO && (
        <div className="asis-center">
          <div className="asis-spinner" />
          <p>Verificando...</p>
        </div>
      )}

      {/* ── RESULTADO ─────────────────────────────────────── */}
      {estado === ESTADO.RESULTADO && persona && (
        <div className="asis-resultado">
          <div className="asis-check">✓ ASISTENCIA REGISTRADA</div>

          {persona.foto
            ? <img className="asis-foto" src={persona.foto} alt={persona.nombre} />
            : <div className="asis-foto-placeholder">Sin foto</div>
          }

          <h1 className="asis-nombre">{persona.nombre}</h1>

          <button className="asis-btn-siguiente" onClick={escanearSiguiente}>
            TOMAR LISTA Y ESCANEAR SIGUIENTE
          </button>
        </div>
      )}

      {/* ── ERROR ─────────────────────────────────────────── */}
      {estado === ESTADO.ERROR && (
        <div className="asis-center asis-error-wrap">
          <div className="asis-error-icon">✕</div>
          <p className="asis-error-msg">{error}</p>
          <button className="asis-btn-siguiente" onClick={escanearSiguiente}>
            ESCANEAR OTRO CÓDIGO QR
          </button>
        </div>
      )}

    </div>
  );
}
